import classNames from 'classnames';
import { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { AnswerRecord, QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import styles from './quiz-preference-select-field.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle as faInfoCircleSolid } from '@fortawesome/pro-solid-svg-icons';
import MoshModal from 'src/components/MoshModal';
import { Badge } from '@get-mosh/msh-shared';
import { env, isMoshy } from 'src/env.mjs';

const QUIZ_PREFERENCE_SELECT_NOT_SURE_ANSWER = {
    answerId: 'preference_select_not_sure',
    answerText: "Not sure, I would like the practitioner's recommendation",
    deselectOthers: true,
};

const QuizPreferenceSelectField = () => {
    const {
        currentQuestion,
        handleSubmitAnswer,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
        state,
    } = useContext(QuizContext);
    const {
        answerList: initialAnswerList,
        questionId,
        questionText,
        questionExplanation,
        additionalField1: modalHeading,
        additionalField2: modalDescription,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        medication,
    } = currentQuestion || {};
    const [pageTransitioning] = state.pageTransitioning;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    // Manually add "not sure" record to answer list
    const answerList = useMemo<AnswerRecord[]>(
        () => [...initialAnswerList, QUIZ_PREFERENCE_SELECT_NOT_SURE_ANSWER],
        [initialAnswerList],
    );

    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (selectedIndexes.length > 0 && !continueDisabled && questionId) {
            handleSubmitAnswer({
                questionId: questionId,
                questionText: questionText,
                answerIds: selectedIndexes.map((i) => answerList[i].answerId),
                answerTexts: selectedIndexes.map(
                    (i) => answerList[i].answerText,
                ),
                medication: medication,
            });
        }
    }, [
        continueDisabled,
        selectedIndexes,
        answerList,
        questionId,
        questionText,
        medication,
        handleSubmitAnswer,
    ]);
    // Handle selecting
    const handleToggleSelected = useCallback(
        (currentIndex: number) => {
            const { deselectOthers } = answerList[currentIndex];
            let newSelectedIndexes = Array.from(selectedIndexes).filter(
                (i) => !answerList[i].deselectOthers,
            );
            if (newSelectedIndexes.includes(currentIndex)) {
                newSelectedIndexes = newSelectedIndexes.filter(
                    (i) => i !== currentIndex,
                );
            } else {
                if (deselectOthers) {
                    newSelectedIndexes = [currentIndex];
                } else {
                    newSelectedIndexes.push(currentIndex);
                }
            }
            setSelectedIndexes(newSelectedIndexes.sort());
        },
        [answerList, selectedIndexes],
    );
    // Set existing answer
    useEffect(() => {
        let selectedAnswerIds: string[] | null = null;
        if (
            questionId &&
            submittedQuestionIds.includes(questionId) &&
            userAnswers.length > 0
        ) {
            selectedAnswerIds = userAnswers.map((a) => a.answerId);
        } else if (
            inQueue &&
            prefillNeeded &&
            prefillQuestionIds.includes(questionId) &&
            prefillAnswers.length > 0
        ) {
            selectedAnswerIds = prefillAnswers.map((a) => a.answerId);
        }
        if (Array.isArray(selectedAnswerIds)) {
            const newSelectedIndexes = selectedAnswerIds.reduce(
                (acc, selectedAnswerId) => [
                    ...acc,
                    answerList.findIndex(
                        (a) => a.answerId === selectedAnswerId,
                    ),
                ],
                [] as number[],
            );
            setSelectedIndexes(newSelectedIndexes);
        } else {
            setSelectedIndexes([]);
        }
    }, [
        questionId,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
        answerList,
    ]);
    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);
    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit]);
    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            setContinueDisabled(selectedIndexes.length === 0);
        }
    }, [selectedIndexes, pageTransitioning, setContinueDisabled]);

    return (
        <div className={classNames(styles['quiz-select-option--wrapper'])}>
            {/* Content header */}
            <div className={classNames(styles['quiz-field-header'])}>
                {/* Title */}
                <h4 className={classNames(styles['title'])}>{questionText}</h4>
                {/* Subtitle */}
                {questionExplanation ? (
                    <h5
                        className={classNames(
                            styles['subtitle'],
                            'highlight pt-2',
                        )}
                    >
                        {questionExplanation}
                    </h5>
                ) : null}
            </div>
            {/* Modal */}
            {modalHeading ? (
                <button
                    className={classNames(
                        styles['quiz-info--wrapper'],
                        'flex items-center gap-x-2',
                        !(modalHeading && modalDescription)
                            ? 'cursor-default'
                            : '',
                    )}
                    onClick={() => {
                        if (modalHeading && modalDescription) {
                            setIsModalOpen(true);
                        }
                    }}
                >
                    {modalHeading && modalDescription ? (
                        <span className="h-6 w-6">
                            <FontAwesomeIcon
                                icon={
                                    isMoshy ? faInfoCircle : faInfoCircleSolid
                                }
                                className="mr-2 !h-full w-full"
                            />
                        </span>
                    ) : null}
                    <h5
                        className={classNames(
                            styles['quiz-info--message'],
                            'highlight',
                        )}
                    >
                        {modalHeading}
                    </h5>
                </button>
            ) : null}
            <MoshModal
                open={isModalOpen}
                ariaLabel={modalHeading}
                onModalClose={(value) => setIsModalOpen(value || false)}
                custom={true}
                theme={`theme-${env.NEXT_PUBLIC_BRAND}`}
            >
                <div>
                    <h4>{modalHeading}</h4>
                    <p className="mb-0 mt-2">{modalDescription}</p>
                </div>
            </MoshModal>
            {/* List of answers */}
            <div className={classNames(styles['answers-list'])}>
                {/* Answer */}
                {answerList.slice(0, answerList.length - 1).map((a, i) => (
                    <button
                        data-testid="quiz-answer"
                        key={a.answerId}
                        className={classNames(
                            'flex',
                            styles['answer-item'],
                            selectedIndexes.includes(i)
                                ? styles['answer-item--selected']
                                : '',
                        )}
                        onClick={() => handleToggleSelected(i)}
                    >
                        <div className="flex flex-col gap-2">
                            {/* Text/description */}
                            <div className="flex flex-col items-start">
                                <span
                                    className={classNames(
                                        styles['answer-item--text'],
                                        'h4',
                                    )}
                                >
                                    {a.answerText}
                                </span>
                                <span
                                    className={classNames(
                                        styles['answer-item--explanation'],
                                        'p1',
                                    )}
                                >
                                    {a.additionalField1}
                                </span>
                            </div>
                            {/* Price */}
                            {a.additionalField2 ? (
                                <div
                                    className={classNames(
                                        'flex items-start',
                                        styles['answer-item--price'],
                                    )}
                                >
                                    <span
                                        className={classNames(
                                            styles['answer-item--price--start'],
                                            'p1 highlight',
                                        )}
                                    >
                                        {a.additionalField2.split('/')[0]}
                                    </span>
                                    <span
                                        className={classNames(
                                            styles['answer-item--price--end'],
                                            'p1',
                                        )}
                                    >
                                        {`/${a.additionalField2.split('/')[1]}`}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                        {/* Badge */}
                        {a.additionalField3 ? (
                            <Badge
                                variant="tag-medium"
                                text={a.additionalField3}
                            />
                        ) : null}
                    </button>
                ))}
                {/* Divider */}
                <span className={classNames(styles['answer-list--divider'])}>
                    or
                </span>
                {/* Fixed "not sure" option */}
                <div className={classNames(styles['answer-item-wrapper'])}>
                    <div
                        className={classNames(
                            'flex',
                            styles['answer-item'],
                            styles['answer-item--not-sure'],
                            selectedIndexes.includes(answerList.length - 1)
                                ? styles['answer-item--selected']
                                : '',
                        )}
                        onClick={() =>
                            handleToggleSelected(answerList.length - 1)
                        }
                    >
                        <span
                            className={classNames(
                                styles['answer-item--text'],
                                'p1 highlight w-full',
                            )}
                        >
                            {QUIZ_PREFERENCE_SELECT_NOT_SURE_ANSWER.answerText}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuizPreferenceSelectField;
